import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

const BLANK_CAT  = { product_id: '', name: '', is_renewal: false, ref_number_required: false, warranty_applicable: false, migration_available: false };
const BLANK_TYPE = { name: '', amount: '', amc_amount: '', dealer_amount: '', dealer_amc_amount: '', duration_months: '', warranty_duration_months: '' };

export default function Categories() {
  const [categories,  setCategories]  = useState([]);
  const [products,    setProducts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [prodFilter,  setProdFilter]  = useState('');
  const [search,      setSearch]      = useState('');

  // Category form modal
  const [catModal,  setCatModal]  = useState(null);  // 'add'|'edit'|'delete'
  const [selCat,    setSelCat]    = useState(null);
  const [catForm,   setCatForm]   = useState(BLANK_CAT);
  const [catSaving, setCatSaving] = useState(false);

  // Types manager modal
  const [typesMgr,    setTypesMgr]    = useState(null);  // category object
  const [types,       setTypes]       = useState([]);
  const [typesLoading,setTypesLoading]= useState(false);
  const [typeForm,    setTypeForm]    = useState(null);   // null | object (id present = edit)
  const [typeErr,     setTypeErr]     = useState('');
  const [typeSaving,  setTypeSaving]  = useState(false);

  const [toast, setToast] = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, pRes] = await Promise.all([
        api.get('/api/categories'),
        api.get('/api/products'),
      ]);
      setCategories(cRes.data);
      setProducts(pRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Category CRUD ──────────────────────────────────────────────────────────

  const openAddCat = () => {
    const def = products[0];
    setCatForm({ ...BLANK_CAT, product_id: def?.id || '' });
    setSelCat(null); setCatModal('add');
  };
  const openEditCat = (c) => {
    setCatForm({ product_id: c.product_id, name: c.name, is_renewal: c.is_renewal, ref_number_required: c.ref_number_required || false, warranty_applicable: c.warranty_applicable || false, migration_available: c.migration_available || false });
    setSelCat(c); setCatModal('edit');
  };
  const openDelCat = (c) => { setSelCat(c); setCatModal('delete'); };
  const closeCat   = () => { setCatModal(null); setSelCat(null); };

  const saveCat = async () => {
    if (!catForm.name.trim())   return showToast('error', 'Category name is required.');
    if (!catForm.product_id)    return showToast('error', 'Select a product.');
    setCatSaving(true);
    try {
      if (selCat) await api.put(`/api/categories/${selCat.id}`, catForm);
      else         await api.post('/api/categories', catForm);
      await loadAll();
      showToast('success', selCat ? 'Category updated.' : 'Category added.');
      closeCat();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Save failed.');
    } finally { setCatSaving(false); }
  };

  const delCat = async () => {
    setCatSaving(true);
    try {
      await api.delete(`/api/categories/${selCat.id}`);
      await loadAll();
      showToast('success', 'Category deleted.');
      closeCat();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed.');
    } finally { setCatSaving(false); }
  };

  // ── Types manager ──────────────────────────────────────────────────────────

  const openTypesMgr = async (cat) => {
    setTypesMgr(cat); setTypeForm(null); setTypeErr('');
    setTypesLoading(true);
    try {
      const r = await api.get(`/api/categories/${cat.id}/types`);
      setTypes(r.data);
    } catch { setTypes([]); } finally { setTypesLoading(false); }
  };

  const reloadTypes = async (catId) => {
    try {
      const r = await api.get(`/api/categories/${catId}/types`);
      setTypes(r.data);
      setCategories(prev => prev.map(c =>
        c.id === catId ? { ...c, type_count: r.data.length } : c
      ));
    } catch {}
  };

  const openAddType  = () => { setTypeForm({ ...BLANK_TYPE }); setTypeErr(''); };
  const openEditType = (t) => {
    setTypeForm({ id: t.id, name: t.name, amount: t.amount ?? '', amc_amount: t.amc_amount ?? '', dealer_amount: t.dealer_amount ?? '', dealer_amc_amount: t.dealer_amc_amount ?? '', duration_months: t.duration_months ?? '', warranty_duration_months: t.warranty_duration_months ?? '' });
    setTypeErr('');
  };
  const cancelType = () => { setTypeForm(null); setTypeErr(''); };

  const saveType = async () => {
    const isRenewal = typesMgr.is_renewal;
    if (!typeForm.name.trim()) return setTypeErr('Type name is required.');
    if (!typeForm.amount || Number(typeForm.amount) <= 0)
      return setTypeErr('Amount is required.');
    if (isRenewal) {
      if (!typeForm.amc_amount || Number(typeForm.amc_amount) <= 0)
        return setTypeErr('AMC amount is required for renewal categories.');
      if (!typeForm.duration_months || Number(typeForm.duration_months) <= 0)
        return setTypeErr('Duration (months) is required for renewal categories.');
    }
    setTypeSaving(true); setTypeErr('');
    try {
      const isWarranty = typesMgr.warranty_applicable;
      const body = {
        name:                     typeForm.name,
        amount:                   Number(typeForm.amount) || null,
        amc_amount:               isRenewal  ? Number(typeForm.amc_amount)               : null,
        dealer_amount:            Number(typeForm.dealer_amount) || null,
        dealer_amc_amount:        isRenewal  ? (Number(typeForm.dealer_amc_amount) || null) : null,
        duration_months:          isRenewal  ? Number(typeForm.duration_months)           : null,
        warranty_duration_months: isWarranty ? (Number(typeForm.warranty_duration_months) || null) : null,
      };
      if (typeForm.id) await api.put(`/api/category-types/${typeForm.id}`, body);
      else              await api.post(`/api/categories/${typesMgr.id}/types`, body);
      await reloadTypes(typesMgr.id);
      setTypeForm(null);
    } catch (e) {
      setTypeErr(e?.response?.data?.detail || 'Save failed.');
    } finally { setTypeSaving(false); }
  };

  const delType = async (tid) => {
    try {
      await api.delete(`/api/category-types/${tid}`);
      await reloadTypes(typesMgr.id);
    } catch (e) {
      setTypeErr(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  // ── Filtered list ──────────────────────────────────────────────────────────

  const filtered = categories.filter(c => {
    const q = search.toLowerCase();
    return (!q || c.name.toLowerCase().includes(q) || c.product_name?.toLowerCase().includes(q))
      && (!prodFilter || String(c.product_id) === String(prodFilter));
  });

  const counts = {
    total:    categories.length,
    renewal:  categories.filter(c => c.is_renewal).length,
    warranty: categories.filter(c => c.warranty_applicable).length,
    types:    categories.reduce((s, c) => s + (c.type_count || 0), 0),
  };

  return (
    <Layout title="Categories">

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* ── Summary row ── */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Categories', val: counts.total,    dot: C.primary  },
          { label: 'Renewal',          val: counts.renewal,  dot: C.warning  },
          { label: 'Warranty',         val: counts.warranty, dot: '#16A34A'  },
          { label: 'Total Types',      val: counts.types,    dot: C.success  },
        ].map(({ label, val, dot }) => (
          <div key={label} style={{
            background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px',
            padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />
            <div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: C.textMain }}>{val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{label}</div>
            </div>
          </div>
        ))}
        <button onClick={openAddCat} style={{ ...btnPrimary, marginLeft: 'auto' }}>
          + Add Category
        </button>
      </div>

      {/* ── Table card ── */}
      <div style={card}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search category or product…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={searchBox}
          />
          <select value={prodFilter} onChange={e => setProdFilter(e.target.value)} style={sel}>
            <option value="">All Products</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {(search || prodFilter) && (
            <button onClick={() => { setSearch(''); setProdFilter(''); }} style={btnGhost}>Clear</button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '12px', color: C.textSub }}>
            {filtered.length} / {categories.length} categories
          </span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '680px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {['Category','Product','Renewal','Ref No. Req.','Warranty','Types','Actions'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={emptyCell}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} style={emptyCell}>
                  {categories.length === 0
                    ? 'No categories yet. Click "+ Add Category" to start.'
                    : 'No results match the current filters.'}
                </td></tr>
              ) : filtered.map((c, i) => (
                <tr key={c.id} style={{ background: i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: `1px solid ${C.border}` }}>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '34px', height: '34px', borderRadius: '8px', flexShrink: 0,
                        background: c.is_renewal ? C.warningBg : C.primaryLight,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px',
                      }}>
                        {c.is_renewal ? '🔄' : '📁'}
                      </div>
                      <span style={{ fontWeight: 600, color: C.textMain }}>{c.name}</span>
                    </div>
                  </td>
                  <td style={td}>
                    <span style={{
                      background: C.primaryLight, color: C.primaryDeep,
                      padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                    }}>
                      📦 {c.product_name || '—'}
                    </span>
                  </td>
                  <td style={td}>
                    {c.is_renewal ? (
                      <span style={{ background: C.warningBg, color: C.warningText, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>
                        🔄 Renewal
                      </span>
                    ) : (
                      <span style={{ color: C.textSub, fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  <td style={td}>
                    {c.ref_number_required ? (
                      <span style={{ background: '#F0F9FF', color: '#0369A1', border: '1px solid #BAE6FD', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        🔢 Required
                      </span>
                    ) : (
                      <span style={{ color: C.textSub, fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  <td style={td}>
                    {c.warranty_applicable ? (
                      <span style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        🛡️ Yes
                      </span>
                    ) : (
                      <span style={{ color: C.textSub, fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  <td style={td}>
                    <button onClick={() => openTypesMgr(c)} style={{
                      background: C.primaryLight, color: C.primaryDeep,
                      border: `1px solid ${C.border}`, borderRadius: '20px',
                      padding: '4px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                    }}>
                      {c.type_count || 0} types ›
                    </button>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <Btn label="Edit"   color="#3B82F6" onClick={() => openEditCat(c)} />
                      <Btn label="Delete" color={C.danger} onClick={() => openDelCat(c)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add / Edit Category Modal ── */}
      {(catModal === 'add' || catModal === 'edit') && (
        <Overlay zIndex={999}>
          <div style={modalBox}>
            <ModalHeader
              title={catModal === 'add' ? 'Add Category' : `Edit — ${selCat?.name}`}
              onClose={closeCat}
            />
            <div style={{ padding: '24px 28px' }}>
              <Field label="Product *">
                <FSelect value={catForm.product_id} onChange={v => setCatForm(f => ({ ...f, product_id: Number(v) }))}>
                  <option value="">— Select Product —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </FSelect>
              </Field>
              <Field label="Category Name *">
                <FInput value={catForm.name} onChange={v => setCatForm(f => ({ ...f, name: v }))} placeholder="e.g. Annual Maintenance Contract" />
              </Field>
              {/* Renewal checkbox */}
              <label style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                border: `1.5px solid ${catForm.is_renewal ? C.primary : C.border}`,
                background: catForm.is_renewal ? C.primaryLight : C.white,
                marginBottom: '10px', userSelect: 'none',
              }}>
                <input
                  type="checkbox"
                  checked={catForm.is_renewal}
                  onChange={e => setCatForm(f => ({ ...f, is_renewal: e.target.checked }))}
                  style={{ width: '18px', height: '18px', accentColor: C.primary, cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>Renewal Category</div>
                  <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>
                    When checked, every type under this category must have an AMC amount and duration.
                  </div>
                </div>
              </label>
              {catForm.is_renewal && (
                <div style={{
                  background: C.warningBg, border: `1px solid ${C.warning}40`,
                  borderRadius: '8px', padding: '10px 14px', marginBottom: '10px',
                  fontSize: '12px', color: C.warningText, display: 'flex', gap: '8px',
                }}>
                  🔄 Types added to this category will require AMC Amount and Duration (months).
                </div>
              )}

              {/* Reference number checkbox */}
              <label style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                border: `1.5px solid ${catForm.ref_number_required ? '#0EA5E9' : C.border}`,
                background: catForm.ref_number_required ? '#F0F9FF' : C.white,
                marginBottom: '4px', userSelect: 'none',
              }}>
                <input
                  type="checkbox"
                  checked={catForm.ref_number_required}
                  onChange={e => setCatForm(f => ({ ...f, ref_number_required: e.target.checked }))}
                  style={{ width: '18px', height: '18px', accentColor: '#0EA5E9', cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>Reference Number Required</div>
                  <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>
                    When checked, products in this category must have a reference number (e.g. IMEI, SIM no., licence key).
                  </div>
                </div>
              </label>
              {catForm.ref_number_required && (
                <div style={{
                  background: '#F0F9FF', border: '1px solid #BAE6FD',
                  borderRadius: '8px', padding: '10px 14px', marginTop: '10px',
                  fontSize: '12px', color: '#0369A1', display: 'flex', gap: '8px',
                }}>
                  🔢 A reference / serial number field will be required when adding inventory items to this category.
                </div>
              )}

              {/* Warranty checkbox */}
              <label style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                border: `1.5px solid ${catForm.warranty_applicable ? '#16A34A' : C.border}`,
                background: catForm.warranty_applicable ? '#F0FDF4' : C.white,
                marginTop: '10px', marginBottom: '4px', userSelect: 'none',
              }}>
                <input
                  type="checkbox"
                  checked={catForm.warranty_applicable}
                  onChange={e => setCatForm(f => ({ ...f, warranty_applicable: e.target.checked }))}
                  style={{ width: '18px', height: '18px', accentColor: '#16A34A', cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>Warranty Applicable</div>
                  <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>
                    When checked, products in this category support warranty tracking (e.g. hardware with manufacturer warranty).
                  </div>
                </div>
              </label>
              {catForm.warranty_applicable && (
                <div style={{
                  background: '#F0FDF4', border: '1px solid #BBF7D0',
                  borderRadius: '8px', padding: '10px 14px', marginTop: '4px',
                  fontSize: '12px', color: '#15803D', display: 'flex', gap: '8px',
                }}>
                  🛡️ Warranty information can be tracked for inventory items in this category.
                </div>
              )}

              {/* Migration available checkbox */}
              <label style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                border: `1.5px solid ${catForm.migration_available ? '#7C3AED' : C.border}`,
                background: catForm.migration_available ? '#F5F3FF' : C.white,
                marginTop: '10px', marginBottom: '4px', userSelect: 'none',
              }}>
                <input
                  type="checkbox"
                  checked={catForm.migration_available}
                  onChange={e => setCatForm(f => ({ ...f, migration_available: e.target.checked }))}
                  style={{ width: '18px', height: '18px', accentColor: '#7C3AED', cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: C.textMain }}>Migration Available</div>
                  <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>
                    When checked, inventory items in this category can be added directly without a Purchase Order (PO).
                  </div>
                </div>
              </label>
              {catForm.migration_available && (
                <div style={{
                  background: '#F5F3FF', border: '1px solid #DDD6FE',
                  borderRadius: '8px', padding: '10px 14px', marginTop: '4px',
                  fontSize: '12px', color: '#5B21B6', display: 'flex', gap: '8px',
                }}>
                  🔄 Items in this category can be added to inventory without raising a PO first.
                </div>
              )}
            </div>
            <ModalFooter onClose={closeCat} onSave={saveCat} saving={catSaving}
              label={catModal === 'add' ? '+ Add Category' : '💾 Update Category'} />
          </div>
        </Overlay>
      )}

      {/* ── Delete Category Modal ── */}
      {catModal === 'delete' && (
        <Overlay zIndex={999}>
          <div style={{ ...modalBox, maxWidth: '400px' }}>
            <ModalHeader title="Delete Category" onClose={closeCat} />
            <div style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: '13px', color: C.textMain, lineHeight: 1.6, margin: 0 }}>
                Delete <strong style={{ color: C.danger }}>{selCat?.name}</strong>?
                <br />
                <span style={{ fontSize: '12px', color: C.textSub }}>
                  This will also delete all {selCat?.type_count || 0} type(s) under it.
                </span>
              </p>
            </div>
            <ModalFooter onClose={closeCat} onSave={delCat} saving={catSaving} label="Delete" danger />
          </div>
        </Overlay>
      )}

      {/* ── Types Manager Modal ── */}
      {typesMgr && (
        <Overlay zIndex={1100}>
          <div style={{ ...modalBox, maxWidth: '640px' }}>
            {/* Header */}
            <div style={{
              padding: '20px 28px', borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
                  <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>
                    Types — {typesMgr.name}
                  </span>
                  {typesMgr.is_renewal && (
                    <span style={{ background: C.warningBg, color: C.warningText, fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px' }}>
                      🔄 RENEWAL
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: C.textSub, marginLeft: '11px' }}>
                  📦 {typesMgr.product_name}
                  {typesMgr.is_renewal && ' · AMC amount & duration required for each type'}
                </div>
              </div>
              <button onClick={() => setTypesMgr(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>

            {/* Add / Edit type form */}
            {typeForm !== null && (
              <div style={{
                padding: '16px 28px', borderBottom: `1px solid ${C.border}`,
                background: C.primaryBg,
              }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: C.primaryDeep, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                  {typeForm.id ? 'Edit Type' : 'Add New Type'}
                </div>
                {typeErr && (
                  <div style={{
                    background: C.dangerBg, color: C.dangerText, border: '1px solid #FECACA',
                    borderRadius: '7px', padding: '8px 12px', marginBottom: '12px', fontSize: '12px',
                  }}>
                    ⚠ {typeErr}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                  <Field label="Type Name *">
                    <FInput value={typeForm.name} onChange={v => setTypeForm(f => ({ ...f, name: v }))} placeholder="e.g. Basic, Premium…" />
                  </Field>
                  <Field label="Amount (₹) *">
                    <FInput type="number" value={typeForm.amount}
                      onChange={v => setTypeForm(f => ({ ...f, amount: v }))}
                      placeholder="0.00" />
                  </Field>
                  <Field label="Dealer Amount (₹)">
                    <FInput type="number" value={typeForm.dealer_amount}
                      onChange={v => setTypeForm(f => ({ ...f, dealer_amount: v }))}
                      placeholder="0.00" />
                  </Field>
                  {typesMgr.is_renewal && (
                    <>
                      <Field label="AMC Amount (₹) *">
                        <FInput type="number" value={typeForm.amc_amount}
                          onChange={v => setTypeForm(f => ({ ...f, amc_amount: v }))}
                          placeholder="0.00" />
                      </Field>
                      <Field label="Dealer AMC Amount (₹)">
                        <FInput type="number" value={typeForm.dealer_amc_amount}
                          onChange={v => setTypeForm(f => ({ ...f, dealer_amc_amount: v }))}
                          placeholder="0.00" />
                      </Field>
                      <Field label="AMC Duration (Months) *">
                        <FInput type="number" value={typeForm.duration_months}
                          onChange={v => setTypeForm(f => ({ ...f, duration_months: v }))}
                          placeholder="12" />
                      </Field>
                    </>
                  )}
                  {typesMgr.warranty_applicable && (
                    <Field label="Warranty (Months)">
                      <FInput type="number" value={typeForm.warranty_duration_months}
                        onChange={v => setTypeForm(f => ({ ...f, warranty_duration_months: v }))}
                        placeholder="12" />
                    </Field>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                  <button onClick={cancelType} style={btnGhost}>Cancel</button>
                  <button onClick={saveType} disabled={typeSaving} style={btnPrimary}>
                    {typeSaving ? 'Saving…' : typeForm.id ? 'Update Type' : 'Add Type'}
                  </button>
                </div>
              </div>
            )}

            {/* Types list */}
            <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
              {typesLoading ? (
                <div style={emptyCell}>Loading types…</div>
              ) : types.length === 0 ? (
                <div style={{ ...emptyCell, padding: '40px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>📋</div>
                  <div style={{ fontSize: '13px', color: C.textSub }}>No types yet. Add your first type below.</div>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                      <th style={th}>Type Name</th>
                      <th style={th}>Amount</th>
                      <th style={th}>Dealer Amount</th>
                      {typesMgr.is_renewal && <th style={th}>AMC Amount</th>}
                      {typesMgr.is_renewal && <th style={th}>Dealer AMC</th>}
                      {typesMgr.is_renewal && <th style={th}>AMC Duration</th>}
                      {typesMgr.warranty_applicable && <th style={th}>Warranty</th>}
                      <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {types.map((t, i) => (
                      <tr key={t.id} style={{ background: i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: `1px solid ${C.border}` }}>
                        <td style={td}>
                          <span style={{ fontWeight: 600, color: C.textMain }}>{t.name}</span>
                        </td>
                        <td style={td}>
                          <span style={{ fontWeight: 700, color: C.primaryDeep }}>
                            {t.amount != null ? `₹${t.amount.toLocaleString()}` : '—'}
                          </span>
                        </td>
                        <td style={td}>
                          <span style={{ fontWeight: 700, color: '#0F766E' }}>
                            {t.dealer_amount != null ? `₹${t.dealer_amount.toLocaleString()}` : '—'}
                          </span>
                        </td>
                        {typesMgr.is_renewal && (
                          <td style={td}>
                            <span style={{ fontWeight: 700, color: '#8B5CF6' }}>
                              {t.amc_amount != null ? `₹${t.amc_amount.toLocaleString()}` : '—'}
                            </span>
                          </td>
                        )}
                        {typesMgr.is_renewal && (
                          <td style={td}>
                            <span style={{ fontWeight: 700, color: '#0F766E' }}>
                              {t.dealer_amc_amount != null ? `₹${t.dealer_amc_amount.toLocaleString()}` : '—'}
                            </span>
                          </td>
                        )}
                        {typesMgr.is_renewal && (
                          <td style={td}>
                            <span style={{
                              background: C.primaryLight, color: C.primaryDeep,
                              padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                            }}>
                              {t.duration_months != null ? `${t.duration_months} months` : '—'}
                            </span>
                          </td>
                        )}
                        {typesMgr.warranty_applicable && (
                          <td style={td}>
                            {t.warranty_duration_months != null ? (
                              <span style={{
                                background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0',
                                padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                              }}>
                                🛡️ {t.warranty_duration_months} months
                              </span>
                            ) : (
                              <span style={{ color: C.textSub, fontSize: '12px' }}>—</span>
                            )}
                          </td>
                        )}
                        <td style={{ ...td, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <Btn label="Edit"   color="#3B82F6" onClick={() => openEditType(t)} />
                            <Btn label="Delete" color={C.danger} onClick={() => delType(t.id)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '14px 28px', borderTop: `1px solid ${C.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <button
                onClick={openAddType}
                disabled={typeForm !== null}
                style={{
                  ...btnPrimary,
                  opacity: typeForm !== null ? 0.5 : 1,
                  cursor: typeForm !== null ? 'not-allowed' : 'pointer',
                }}
              >
                + Add Type
              </button>
              <button onClick={() => setTypesMgr(null)} style={btnGhost}>Close</button>
            </div>
          </div>
        </Overlay>
      )}
    </Layout>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function Btn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
      background: `${color}18`, color, border: `1px solid ${color}30`, cursor: 'pointer',
    }}>{label}</button>
  );
}

function Overlay({ children, zIndex = 999 }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex, padding: '20px',
    }}>{children}</div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div style={{
      padding: '22px 28px', borderBottom: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
        <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>{title}</span>
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
    </div>
  );
}

function ModalFooter({ onClose, onSave, saving, label, danger = false }) {
  return (
    <div style={{
      padding: '16px 28px', borderTop: `1px solid ${C.border}`,
      display: 'flex', justifyContent: 'flex-end', gap: '10px',
    }}>
      <button onClick={onClose} style={btnGhost}>Cancel</button>
      <button onClick={onSave} disabled={saving}
        style={danger ? btnDanger : btnPrimary}>
        {saving ? 'Saving…' : label}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function FInput({ type = 'text', value, onChange, placeholder }) {
  const [focus, setFocus] = useState(false);
  return (
    <input type={type} value={value ?? ''} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{ ...inputBase, borderColor: focus ? C.primary : C.border }} />
  );
}

function FSelect({ value, onChange, children }) {
  return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value)}
      style={{ ...inputBase, background: C.white, cursor: 'pointer', borderColor: C.border }}>
      {children}
    </select>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputBase = {
  width: '100%', padding: '9px 12px', border: '1.5px solid', borderRadius: '8px',
  fontSize: '13px', color: C.textMain, outline: 'none',
  boxSizing: 'border-box', transition: 'border-color 0.15s',
};
const card      = { background: C.white, borderRadius: '12px', padding: '22px', border: `1px solid ${C.border}` };
const modalBox  = { background: C.white, borderRadius: '16px', width: '100%', maxWidth: '560px', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}` };
const th        = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td        = { padding: '13px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell = { textAlign: 'center', padding: '48px', color: C.textSub, fontSize: '13px' };
const searchBox = { flex: 1, minWidth: '200px', padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' };
const sel       = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnPrimary = {
  padding: '10px 20px',
  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
  color: '#fff', border: 'none', borderRadius: '8px',
  fontSize: '13px', fontWeight: 700, cursor: 'pointer',
  boxShadow: `0 3px 10px rgba(245,166,35,0.3)`,
};
const btnGhost = {
  padding: '10px 18px', background: C.primaryBg, color: C.textSub,
  border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
};
const btnDanger = {
  padding: '10px 18px', background: C.danger, color: '#fff', border: 'none',
  borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
};
